import React, { useMemo, useState } from 'react';
import { OrderTransaction, PaginationInfo } from '../../services/creditTransactionService';
import TransactionCard from './TransactionCard';
import Pagination from './Pagination';

interface OrderHistoryTabProps {
    orderHistory: OrderTransaction[];
    dateRange: { start: Date | null; end: Date | null };
    searchTerm: string;
}

const OrderHistoryTab: React.FC<OrderHistoryTabProps> = ({
    orderHistory,
    dateRange,
    searchTerm
}) => {
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Filter transactions based on date range and search term
    const filteredTransactions = useMemo(() => {
        const filtered = orderHistory.filter(transaction => {
            // Date filter
            const transactionDate = new Date(transaction.created_at);
            if (dateRange.start && transactionDate < dateRange.start) return false;
            if (dateRange.end && transactionDate > dateRange.end) return false;

            // Search filter
            if (searchTerm) {
                const searchLower = searchTerm.toLowerCase();
                return (
                    transaction.plan_display_name.toLowerCase().includes(searchLower) ||
                    transaction.id.toLowerCase().includes(searchLower) ||
                    transaction.status.toLowerCase().includes(searchLower)
                );
            }

            return true;
        });

        // Sort by date (newest first)
        return filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }, [orderHistory, dateRange, searchTerm]);

    // Reset to first page when filters change
    React.useEffect(() => {
        setCurrentPage(1);
    }, [dateRange, searchTerm]);

    // Calculate pagination info
    const paginationInfo: PaginationInfo = useMemo(() => {
        const totalItems = filteredTransactions.length;
        const totalPages = Math.ceil(totalItems / itemsPerPage);
        const hasNextPage = currentPage < totalPages;
        const hasPreviousPage = currentPage > 1;

        return {
            currentPage,
            totalPages,
            totalItems,
            itemsPerPage,
            hasNextPage,
            hasPreviousPage
        };
    }, [filteredTransactions.length, itemsPerPage, currentPage]);

    // Get current page transactions
    const currentPageTransactions = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredTransactions.slice(startIndex, endIndex);
    }, [filteredTransactions, currentPage, itemsPerPage]);

    if (filteredTransactions.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <i className="fas fa-receipt text-4xl text-gray-300 mb-4"></i>
                <h3 className="text-lg font-medium text-text-heading mb-2">
                    No purchase history found
                </h3>
                <p className="text-text-description">
                    {searchTerm || dateRange.start || dateRange.end
                        ? "Try adjusting your filters to find transactions."
                        : "You haven't made any credit purchases yet."}
                </p>
            </div>
        );
    }

    return (
        <div className="w-full">
            <div className="space-y-4">
                {currentPageTransactions.map((transaction) => (
                    <TransactionCard
                        key={transaction.id}
                        transaction={{
                            id: transaction.id,
                            date: transaction.created_at,
                            type: 'purchase',
                            title: transaction.plan_display_name,
                            subtitle: `Order Id : ${transaction.id}`,
                            amount: transaction.credits_received,
                            price: transaction.amount,
                            currency: transaction.currency,
                            status: transaction.status,
                            icon: 'fas fa-plus-circle',
                            iconColor: 'text-secondary-600',
                            iconBg: 'bg-secondary-100'
                        }}
                    />
                ))}
            </div>

            {paginationInfo.totalPages > 1 && (
                <Pagination
                    currentPage={paginationInfo.currentPage}
                    totalPages={paginationInfo.totalPages}
                    totalItems={paginationInfo.totalItems}
                    itemsPerPage={paginationInfo.itemsPerPage}
                    hasNextPage={paginationInfo.hasNextPage}
                    hasPreviousPage={paginationInfo.hasPreviousPage}
                    onPageChange={setCurrentPage}
                    onItemsPerPageChange={(newItemsPerPage) => {
                        setItemsPerPage(newItemsPerPage);
                        setCurrentPage(1);
                    }}
                />
            )}
        </div>
    );
};

export default OrderHistoryTab; 